/**
 * All production systems and proprietary products engineered by HUN TECH PLC.
 * Single source of truth — strictly typed and configuration-driven.
 */

export type ProductStatus =
  | 'In Development'
  | 'Planning Phase'
  | 'Production Foundation'
  | 'Coming Soon'

export type ProductCategory =
  | 'All'
  | 'Mobile Platforms'
  | 'Enterprise & ERP'
  | 'Retail & Commerce'

export interface BentoWorkflow {
  readonly badge: string
  readonly title: string
  readonly description: string
  readonly componentLabel: string
}

export interface BentoMetric {
  readonly badge: string
  readonly title: string
  readonly description: string
  readonly value: string
  readonly subtext: string
}

export interface BentoConnectivityGateway {
  readonly name: string
  readonly status: string
}

export interface BentoConnectivity {
  readonly badge: string
  readonly title: string
  readonly description: string
  readonly gateways: readonly BentoConnectivityGateway[]
}

export interface BentoTelemetry {
  readonly badge: string
  readonly title: string
  readonly description: string
  readonly bars: readonly number[]
}

export interface ProductBento {
  readonly mainHeading: string
  readonly subHeading: string
  readonly workflow: BentoWorkflow
  readonly metric: BentoMetric
  readonly connectivity: BentoConnectivity
  readonly telemetry: BentoTelemetry
}

export interface EnginePipelineStep {
  readonly stepNumber: string
  readonly title: string
  readonly description: string
}

export type Product = {
  readonly id: string
  readonly name: string
  readonly amharicName?: string
  readonly category: Exclude<ProductCategory, 'All'>
  readonly tagline: string
  readonly status: ProductStatus
  readonly platforms: readonly string[]
  readonly techStack: readonly string[]
  readonly description: string
  readonly targetAudience: string
  readonly keyCapabilities: readonly string[]
  readonly headline: string
  readonly actionStatement: string
  readonly bento: ProductBento
  readonly engineSteps: readonly [EnginePipelineStep, EnginePipelineStep, EnginePipelineStep]
  readonly schemaFilename: string
  readonly schemaJson: Record<string, unknown>
}

export const products: readonly Product[] = [
  {
    id: 'suke',
    name: 'SUKE',
    amharicName: 'ሱቄ',
    category: 'Mobile Platforms',
    tagline: 'Cloud-Powered Retail & Inventory Management in Your Pocket',
    status: 'In Development',
    platforms: ['Mobile App (iOS & Android)'],
    techStack: ['React Native', 'Expo', 'Cloud API', 'Offline Buffer', 'SQLite'],
    headline: 'Pocket Retail & POS Management.',
    actionStatement: 'Cloud-Buffered Native Point-of-Sale with Instant Telebirr Sync',
    description:
      'A dedicated mobile application engineered to power daily retail operations for local boutiques, shoe stores, apparel shops, and mid-sized neighborhood mini-marts. It converts any mobile device into a resilient point-of-sale and stock management terminal.',
    targetAudience:
      'Small to mid-size retail shops, supermarkets, clothing stores, and independent merchants.',
    keyCapabilities: [
      'Pocket Point-of-Sale (POS) for instant sales checkouts',
      'Real-time inventory ledger with low-stock replenishment alerts',
      'Automated daily gross sales, margin, and cash-flow reporting',
      'Offline-first resilience designed for low or intermittent connectivity',
      'Barcode camera scanning for rapid stock auditing',
    ],
    bento: {
      mainHeading: 'Engineered to optimize modern operation speeds.',
      subHeading: '// System Capabilities',
      workflow: {
        badge: 'Real-Time Till',
        title: 'Core Application Workflow',
        description:
          'Instant optical camera scanning, real-time basket tax calculation, and automated margin ledger tracking.',
        componentLabel: 'Live Cart Automation & Offline Ledger',
      },
      metric: {
        badge: 'Local Latency',
        title: 'Offline Execution Speed',
        description:
          'Transactions commit to an encrypted local SQLite buffer before streaming upstream over intermittent cellular links.',
        value: '< 12ms',
        subtext: '▲ Local offline ledger commit turnaround',
      },
      connectivity: {
        badge: 'Hardware & Rails',
        title: 'Peripheral & Payment Gateways',
        description:
          'Native hardware coupling for mobile bluetooth thermal printers and Ethiopian digital payment infrastructure.',
        gateways: [
          { name: 'Telebirr USSD & QR', status: 'OPERATIONAL' },
          { name: 'CBE Birr Gateway', status: 'OPERATIONAL' },
          { name: 'Bluetooth Thermal ESC/POS', status: 'COMPLIANT' },
          { name: 'Offline Sync Buffer', status: 'ACTIVE' },
        ],
      },
      telemetry: {
        badge: 'Daily Velocity',
        title: 'Store Inventory & Cash Flow Telemetry',
        description:
          'Hourly sales volume, gross margin calculations, and inventory turnover distribution monitored in real-time.',
        bars: [35, 60, 45, 80, 65, 90, 75, 95],
      },
    },
    engineSteps: [
      {
        stepNumber: '01',
        title: 'Offline Transaction Buffering',
        description:
          'Sales events and receipt items are validated and cryptographically committed to local storage when network coverage drops.',
      },
      {
        stepNumber: '02',
        title: 'Cryptographic Sync Reconciler',
        description:
          'Re-establishes socket connection and streams idempotent transaction logs to cloud ledger without duplicate entries.',
      },
      {
        stepNumber: '03',
        title: 'Multi-Store Inventory Ledger Sync',
        description:
          'Reconciles stock balances across all till terminals, fires low-stock triggers, and updates the owner executive view.',
      },
    ],
    schemaFilename: 'suke-pos-contract.json',
    schemaJson: {
      $schema: 'https://hun.tech/schemas/suke-pos-v1.json',
      systemEngine: {
        id: 'suke-mobile-pos',
        version: '1.4.0',
        mode: 'offline_first_reconciler',
        database: {
          local: 'sqlite-cipher-v4',
          cloudSync: 'event-stream-websocket',
          encryption: 'AES-256-GCM',
        },
        peripherals: {
          thermalPrinter: 'ESC/POS 58mm/80mm Bluetooth',
          cameraScanner: 'EAN-13 / Code-128 / QR',
        },
        paymentHooks: ['telebirr_direct', 'cbe_birr', 'cash_reconciliation'],
      },
    },
  },
  {
    id: 'derash-ride',
    name: 'DerashRide',
    amharicName: 'ደራሽ ራይድ',
    category: 'Mobile Platforms',
    tagline: 'On-Demand Urban Mobility & Fleet Dispatch Ecosystem',
    status: 'Planning Phase',
    platforms: ['Mobile App (Riders & Drivers)', 'Central Web Dispatcher'],
    techStack: ['React Native', 'Expo', 'Web Dashboard', 'Real-Time Telemetry', 'Redis'],
    headline: 'Urban Mobility & Fleet Dispatch Ecosystem.',
    actionStatement: 'High-Throughput Geospatial Matchmaker & Automated Fare Engine',
    description:
      'A modern urban transportation platform unifying a dual-profile mobile architecture for passengers and drivers with an executive web dispatch console for fleet management, route analytics, and automated fare settlement.',
    targetAudience:
      'Urban commuters, professional drivers, fleet operators, and transit organizations.',
    keyCapabilities: [
      'Dual-mode client application with seamless passenger-driver roles',
      'Dynamic geospatial route calculation and distance metering',
      'Web-based central operations console for dispatchers and fleet managers',
      'Multi-channel payment handling (Telebirr, local banking, cash reconciliation)',
      'Automated driver verification, trip history, and commission accounting',
    ],
    bento: {
      mainHeading: 'Sub-second geospatial indexing across urban transit corridors.',
      subHeading: '// Mobility Engine',
      workflow: {
        badge: 'Spatial Index',
        title: 'Geospatial Dispatch Pipeline',
        description:
          'Sub-second spatial indexing matching passenger requests to the closest verified driver using localized road-graph routing.',
        componentLabel: 'Real-Time Fleet Grid Simulator',
      },
      metric: {
        badge: 'Dispatch Speed',
        title: 'Matching Engine Latency',
        description:
          'Turnaround latency for querying nearest driver coordinates across 10,000+ concurrent GPS telemetry streams.',
        value: '48ms',
        subtext: '▲ P99 driver dispatch turnaround',
      },
      connectivity: {
        badge: 'Transit Network',
        title: 'Telemetry & Settlement Connectors',
        description:
          'Bidirectional WebSockets with GPS telemetry fallback and automated Ethiopian mobile money escrow.',
        gateways: [
          { name: 'GPS WebSocket Stream', status: 'OPERATIONAL' },
          { name: 'Telebirr Fare Escrow', status: 'COMPLIANT' },
          { name: 'CBE Bank Transfer API', status: 'OPERATIONAL' },
          { name: 'SMS Trip Fallback', status: 'ACTIVE' },
        ],
      },
      telemetry: {
        badge: 'Fleet Volume',
        title: 'Concurrent Ride Telemetry & Surge Index',
        description:
          'Real-time trip volume, driver availability clusters, and peak zone dynamic multipliers monitored continuously.',
        bars: [40, 55, 70, 95, 85, 60, 75, 90],
      },
    },
    engineSteps: [
      {
        stepNumber: '01',
        title: 'Geospatial Proximity Ingestion',
        description:
          'High-frequency GPS pings stream via WebSockets into Redis geospatial memory buffers and road snapping models.',
      },
      {
        stepNumber: '02',
        title: 'Bipartite Trip Matching & Route Optimization',
        description:
          'Algorithmic solver calculates optimal driver assignment, arrival ETA, and fixed distance/time tariff structure.',
      },
      {
        stepNumber: '03',
        title: 'Automated Fare Settlement & Split',
        description:
          'Executes multi-party transaction split allocating platform commission and driver wallet payouts with digital receipts.',
      },
    ],
    schemaFilename: 'derash-dispatch.json',
    schemaJson: {
      $schema: 'https://hun.tech/schemas/derash-dispatch-v1.json',
      systemEngine: {
        id: 'derash-mobility-engine',
        version: '2.1.0',
        spatialIndexer: 'H3-geospatial-index',
        telemetrySocket: 'wss://stream.derash.hun.tech/v1/driver',
        fareMatrix: {
          baseTariff: 'ETB 150.00',
          perKilometer: 'ETB 28.50',
          peakSurgeMultiplier: 1.25,
        },
        settlementRails: ['telebirr_escrow', 'cbe_direct', 'cash_reconciler'],
      },
    },
  },
  {
    id: 'hun-core-erp',
    name: 'HUN CoreERP',
    category: 'Enterprise & ERP',
    tagline: 'The Modular Enterprise Foundation for Regional Organizations',
    status: 'Production Foundation',
    platforms: ['Web Enterprise Portal', 'Role-Based Desktop'],
    techStack: ['React', 'TypeScript', 'Relational SQL', 'Hono Microservices', 'Tailwind'],
    headline: 'Modular Enterprise Operations & ERP.',
    actionStatement: 'Event-Driven ERP Architecture with Ethiopian Statutory Payroll & Inventory Ledgers',
    description:
      'A battle-tested modular ERP foundation engineered to be rapidly tailored and deployed for enterprises seeking unified business management without paying exorbitant recurring fees for rigid foreign software.',
    targetAudience:
      'Manufacturing plants, wholesale distributors, logistics firms, and commercial enterprises.',
    keyCapabilities: [
      'Multi-warehouse inventory logistics and bin-level tracking',
      'Procurement automation, purchase order approvals, and vendor ledgers',
      'Ethiopian statutory payroll engine with local tax & pension computation',
      'General ledger, accounts receivable/payable, and automated invoicing',
      'Granular role-based access control (RBAC) and immutable audit trails',
    ],
    bento: {
      mainHeading: 'Audited financial compliance and real-time inventory reconciliation.',
      subHeading: '// Enterprise Core',
      workflow: {
        badge: 'Ledger Flow',
        title: 'Multi-Entity Financial Workflow',
        description:
          'Strict double-entry bookkeeping, multi-tier purchase authorization rules, and bin-level warehouse tracking.',
        componentLabel: 'Automated Voucher & General Ledger Pipeline',
      },
      metric: {
        badge: 'Data Integrity',
        title: 'ACID Transaction Throughput',
        description:
          'Deterministic financial ledger processing with zero tolerance for floating-point balance discrepancies.',
        value: '99.999%',
        subtext: '▲ Balance sheet & trial balance audit consistency',
      },
      connectivity: {
        badge: 'Compliance & APIs',
        title: 'Statutory & Enterprise Connectors',
        description:
          'Pre-configured compliance bridges for Ethiopian tax authority reporting and corporate financial institutions.',
        gateways: [
          { name: 'ERCA Tax & Pension Engine', status: 'COMPLIANT' },
          { name: 'Electronic Signature Node', status: 'OPERATIONAL' },
          { name: 'Bank Statement Auto-Reconciler', status: 'OPERATIONAL' },
          { name: 'Immutable Audit Log Vault', status: 'ACTIVE' },
        ],
      },
      telemetry: {
        badge: 'Fiscal Velocity',
        title: 'Procurement & Working Capital Trends',
        description:
          'Monthly operating expense burn, accounts receivable aging, and working capital liquidity distribution.',
        bars: [60, 45, 75, 85, 50, 90, 80, 70],
      },
    },
    engineSteps: [
      {
        stepNumber: '01',
        title: 'Strict Double-Entry Ingestion',
        description:
          'Every voucher and invoice is ingested with cryptographically signed journal records and balanced debits/credits.',
      },
      {
        stepNumber: '02',
        title: 'Statutory Tax & Pension Deduction Calculation',
        description:
          'Applies Ethiopian progressive income tax brackets, employee pension (7%), and employer contribution (11%).',
      },
      {
        stepNumber: '03',
        title: 'Immutable General Ledger Posting',
        description:
          'Commits entries to audit-proof relational storage and updates corporate financial reports instantly.',
      },
    ],
    schemaFilename: 'core-erp-architecture.json',
    schemaJson: {
      $schema: 'https://hun.tech/schemas/core-erp-v1.json',
      systemEngine: {
        id: 'hun-core-erp',
        architecture: 'modular-domain-driven',
        modules: [
          'general_ledger',
          'ethiopian_payroll',
          'warehouse_logistics',
          'procurement_approvals',
        ],
        statutoryCompliance: {
          jurisdiction: 'ET',
          pensionEmployerRate: 0.11,
          pensionEmployeeRate: 0.07,
          incomeTaxBrackets: 'Proclamation-979/2016',
        },
        auditTrail: 'immutable_append_only_log',
      },
    },
  },
  {
    id: 'hun-speed-pos',
    name: 'HUN SpeedPOS',
    category: 'Retail & Commerce',
    tagline: 'High-Velocity Universal Commercial Point-of-Sale Engine',
    status: 'Coming Soon',
    platforms: ['Desktop Terminal', 'Tablet POS', 'Touch Displays'],
    techStack: ['React', 'Electron / Web', 'SQLite Local Cache', 'Cloud Sync', 'ESC/POS'],
    headline: 'High-Velocity Counter Checkout Terminal.',
    actionStatement: 'Hardware-Coupled Counter Terminal with Zero-Latency Offline Cashier Lock',
    description:
      'A universal, lightweight, and ultra-reliable counter point-of-sale system engineered for high-throughput commercial checkout environments where downtime is unacceptable.',
    targetAudience:
      'Supermarkets, retail store chains, hospitality counters, and high-traffic merchants.',
    keyCapabilities: [
      'Instant barcode scanning and thermal hardware receipt printing',
      'Autonomous offline sales buffer with zero data loss during network outages',
      'Multi-counter synchronization and shift-by-shift till reconciliation',
      'Native integration with local digital payment processors (Telebirr, CBE Birr)',
      'Hardware peripheral support for scales, cash drawers, and customer displays',
    ],
    bento: {
      mainHeading: 'Sub-millisecond checkout velocity built for peak rush-hour queues.',
      subHeading: '// Retail Velocity',
      workflow: {
        badge: 'High-Throughput Till',
        title: 'Rapid Counter Checkout Loop',
        description:
          'Sub-second 1D/2D optical barcode decoding with instant thermal print spooling and pole customer display mirroring.',
        componentLabel: 'Hardware Barcode & ESC/POS Spooler',
      },
      metric: {
        badge: 'Hardware Bus',
        title: 'Scan-to-Print Latency',
        description:
          'Total roundtrip latency from laser optical trigger to ESC/POS thermal receipt line feed.',
        value: '18ms',
        subtext: '▲ Hardware peripheral roundtrip latency',
      },
      connectivity: {
        badge: 'Peripheral Bus',
        title: 'Peripheral Drivers & Counter Rails',
        description:
          'Native low-level serial and USB driver communication for high-volume counter hardware suites.',
        gateways: [
          { name: 'USB/Serial ESC/POS Thermal', status: 'OPERATIONAL' },
          { name: 'RJ11 Cash Drawer Kick', status: 'COMPLIANT' },
          { name: 'Telebirr QR Pole Display', status: 'OPERATIONAL' },
          { name: 'RS232 Certified Scale', status: 'ACTIVE' },
        ],
      },
      telemetry: {
        badge: 'Lane Velocity',
        title: 'Counter Lane Throughput & Till Volume',
        description:
          'Items scanned per minute across all active cashier lanes during peak shopping hours.',
        bars: [50, 70, 85, 95, 65, 80, 90, 100],
      },
    },
    engineSteps: [
      {
        stepNumber: '01',
        title: 'Hardware Event Capture',
        description:
          'Intercepts raw HID barcode scanner inputs and scale serial streams with zero UI thread contention.',
      },
      {
        stepNumber: '02',
        title: 'In-Memory Catalog & Tier Discount Lookup',
        description:
          'Executes catalog matching, bulk volume tier pricing, and VAT computation locally in sub-millisecond buffers.',
      },
      {
        stepNumber: '03',
        title: 'Atomic Receipt & Payment Settlement',
        description:
          'Triggers cash drawer pulse kick, prints thermal customer slip, and queues asynchronous cloud sync packet.',
      },
    ],
    schemaFilename: 'speed-pos-hardware.json',
    schemaJson: {
      $schema: 'https://hun.tech/schemas/speed-pos-v1.json',
      systemEngine: {
        id: 'hun-speed-pos-core',
        runtime: 'cross-platform-native',
        offlineResilience: 'local-sqlite-cache',
        peripherals: {
          barcodeReader: 'USB-HID / Virtual-COM',
          thermalPrinter: 'ESC/POS-Direct-9100',
          cashDrawer: '24V-RJ11-Pulse',
          customerDisplay: 'VFD-20x2 / SecondScreen',
        },
        paymentProtocols: ['telebirr_pos_qr', 'cbe_birr', 'pos_card_terminal'],
      },
    },
  },
  {
    id: 'hun-commerce',
    name: 'HUN Commerce',
    category: 'Retail & Commerce',
    tagline: 'Turnkey Customer Mobile Storefront & Merchant Operations Suite',
    status: 'In Development',
    platforms: ['Customer Mobile App (iOS & Android)', 'Merchant Web Dashboard'],
    techStack: ['React Native', 'Expo', 'React Web Admin', 'Payment APIs', 'PostgreSQL'],
    headline: 'Turnkey Mobile Storefront & Operations.',
    actionStatement: 'Omnichannel Direct-to-Consumer Engine with Unified Catalog & Order Fulfillment',
    description:
      'A comprehensive commercial commerce solution that equips retail brands with their own native mobile shopping application for consumers paired with an executive web dashboard for catalog and order fulfillment management.',
    targetAudience:
      'Direct-to-consumer retail brands, fashion boutiques, electronics distributors, and expanding businesses.',
    keyCapabilities: [
      'Branded native mobile storefront with product catalog and visual search',
      'Smooth checkout workflow integrated with Telebirr, Chapa, and CBE gateways',
      'Executive web portal for catalog editing, stock allocation, and promos',
      'Live order dispatch pipeline with customer delivery status notifications',
      'Customer profile management, order history, and digital receipts',
    ],
    bento: {
      mainHeading: 'Frictionless consumer shopping paired with enterprise fulfillment.',
      subHeading: '// Omnichannel Suite',
      workflow: {
        badge: 'Fulfillment Flow',
        title: 'End-to-End Fulfillment Pipeline',
        description:
          'Instant mobile push notifications upon purchase, merchant picking checklist, and automated courier route dispatch.',
        componentLabel: 'Live Order Status & Dispatch Pipeline',
      },
      metric: {
        badge: 'Checkout Speed',
        title: 'Mobile Checkout Velocity',
        description:
          'Average elapsed duration from item discovery and cart insertion to completed digital payment authorization.',
        value: '24s',
        subtext: '▲ Average consumer checkout turnaround',
      },
      connectivity: {
        badge: 'Ecosystem Rails',
        title: 'Multi-Channel Gateway Support',
        description:
          'Unified connectors for digital payment gateways, SMS notifications, and delivery routing webhooks.',
        gateways: [
          { name: 'Telebirr Direct Checkout', status: 'OPERATIONAL' },
          { name: 'Chapa Payment Gateway', status: 'OPERATIONAL' },
          { name: 'SMS Gateway (Ethio Telecom)', status: 'ACTIVE' },
          { name: 'Courier Webhook Dispatch', status: 'COMPLIANT' },
        ],
      },
      telemetry: {
        badge: 'Conversion Rate',
        title: 'Catalog Funnel & Cart Velocity',
        description:
          'Real-time cart addition rate, checkout conversion %, and repeat customer metrics monitored across mobile storefronts.',
        bars: [30, 45, 65, 80, 70, 85, 90, 75],
      },
    },
    engineSteps: [
      {
        stepNumber: '01',
        title: 'Mobile Catalog Sync & Real-Time Stock',
        description:
          'Synchronizes merchandise variants, high-res assets, and live inventory allocations across consumer mobile clients.',
      },
      {
        stepNumber: '02',
        title: 'Escrow Payment Authorization',
        description:
          'Processes digital transaction through Telebirr or Chapa, verifying funds prior to merchant fulfillment notification.',
      },
      {
        stepNumber: '03',
        title: 'Merchant Picking & Courier Handoff',
        description:
          'Generates digital packing slip, dispatches order status notifications, and coordinates courier pickup route.',
      },
    ],
    schemaFilename: 'hun-commerce-spec.json',
    schemaJson: {
      $schema: 'https://hun.tech/schemas/hun-commerce-v1.json',
      systemEngine: {
        id: 'hun-commerce-platform',
        architecture: 'headless-omnichannel',
        clients: {
          consumerApp: 'React Native (iOS / Android)',
          merchantPortal: 'React Web Admin',
        },
        paymentProcessors: ['telebirr', 'chapa', 'cbe_birr'],
        fulfillment: {
          packingSlipGeneration: 'automated',
          courierDispatchWebhook: 'enabled',
          smsNotification: 'ethio_telecom_gateway',
        },
      },
    },
  },
] as const
